"use client";
import React, { useState } from "react";
import { Box, Button, SxProps, Theme } from "@mui/material";
import ReactShowMoreText from "react-show-more-text";
import { useTranslate } from "@/hook/useTranslate";

// 구글 번역 필수 문구 (각 언어별)
const GOOGLE_TRANSLATE_ATTRIBUTION = {
  en: "Powered by Google Translate",
  de: "Unterstützt von Google Übersetzer",
  ko: "Google 번역 제공",
  ja: "Google 翻訳を使用",
  "zh-cn": "由谷歌翻译提供支持",
  "zh-tw": "由 Google 翻譯提供技術支援",
  "zh-hk": "由 Google 翻譯提供技術支援",
  es: "Con tecnología de Google Traductor",
  fr: "Fourni par Google Traduction",
  pt: "Powered by Google Tradutor",
  ru: "Технология Google Переводчик",
  ar: "مدعوم من ترجمة Google",
  hi: "Google अनुवाद द्वारा संचालित",
  vi: "Được hỗ trợ bởi Google Dịch",
  th: "ขับเคลื่อนโดย Google แปลภาษา",
  id: "Didukung oleh Google Terjemahan",
};

// 더보기 텍스트 (각 언어별)
const SHOW_MORE_TEXT = {
  en: "Show more",
  de: "Mehr anzeigen",
  ko: "자세히 보기",
  ja: "もっと見る",
  "zh-cn": "显示更多",
  "zh-tw": "顯示更多",
  "zh-hk": "顯示更多",
  es: "Mostrar más",
  fr: "Afficher plus",
  pt: "Mostrar mais",
  ru: "Показать больше",
  ar: "عرض المزيد",
  hi: "और दिखाएं",
  vi: "Hiển thị thêm",
  th: "แสดงเพิ่มเติม",
  id: "Tampilkan lebih banyak",
};

// 번역하기 버튼 텍스트 (각 언어별)
const TRANSLATE_TEXT = {
  en: "Translate",
  de: "Übersetzen",
  ko: "번역하기",
  ja: "翻訳する",
  "zh-cn": "翻译",
  "zh-tw": "翻譯",
  "zh-hk": "翻譯",
  es: "Traducir",
  fr: "Traduire",
  pt: "Traduzir",
  ru: "Перевести",
  ar: "ترجمة",
  hi: "अनुवाद करें",
  vi: "Dịch",
  th: "แปล",
  id: "Terjemahkan",
};

// 원문 보기 버튼 텍스트 (각 언어별)
const SHOW_ORIGINAL_TEXT = {
  en: "Show original",
  de: "Original anzeigen",
  ko: "원문 보기",
  ja: "原文を表示",
  "zh-cn": "显示原文",
  "zh-tw": "顯示原文",
  "zh-hk": "顯示原文",
  es: "Mostrar original",
  fr: "Afficher l'original",
  pt: "Mostrar original",
  ru: "Показать оригинал",
  ar: "عرض الأصل",
  hi: "मूल दिखाएं",
  vi: "Hiển thị bản gốc",
  th: "แสดงต้นฉบับ",
  id: "Tampilkan asli",
};

// 번역 중 텍스트 (각 언어별)
const TRANSLATING_TEXT = {
  en: "Translating...",
  de: "Übersetzen...",
  ko: "번역 중...",
  ja: "翻訳中...",
  "zh-cn": "翻译中...",
  "zh-tw": "翻譯中...",
  "zh-hk": "翻譯中...",
  es: "Traduciendo...",
  fr: "Traduction...",
  pt: "Traduzindo...",
  ru: "Перевод...",
  ar: "جاري الترجمة...",
  hi: "अनुवाद कर रहे हैं...",
  vi: "Đang dịch...",
  th: "กำลังแปล...",
  id: "Menerjemahkan...",
};

interface GoogleTranslateShowMoreTextProps {
  text: string;
  maxLines?: number;
  sourceLang: string;
  targetLang: string;
  expanded?: boolean;
  sxProps?: SxProps<Theme>;
}

const GoogleTranslateShowMoreText = ({
  text,
  maxLines = 10,
  sourceLang,
  targetLang,
  expanded = true,
  sxProps,
}: GoogleTranslateShowMoreTextProps) => {
  const [translatedText, setTranslatedText] = useState<string>("");
  const [isTranslating, setIsTranslating] = useState<boolean>(false);
  const [showTranslation, setShowTranslation] = useState<boolean>(false);
  const [hasTranslated, setHasTranslated] = useState<boolean>(false);

  const onTranslate = useTranslate();
  const needsTranslation = sourceLang !== targetLang;

  // 언어별 문구 가져오기 헬퍼
  const getShowMoreText = (lang: string) => {
    return SHOW_MORE_TEXT[lang as keyof typeof SHOW_MORE_TEXT] || SHOW_MORE_TEXT.en;
  };
  const getTranslateText = (lang: string) => {
    return TRANSLATE_TEXT[lang as keyof typeof TRANSLATE_TEXT] || TRANSLATE_TEXT.en;
  };
  const getShowOriginalText = (lang: string) => {
    return SHOW_ORIGINAL_TEXT[lang as keyof typeof SHOW_ORIGINAL_TEXT] || SHOW_ORIGINAL_TEXT.en;
  };
  const getTranslatingText = (lang: string) => {
    return TRANSLATING_TEXT[lang as keyof typeof TRANSLATING_TEXT] || TRANSLATING_TEXT.en;
  };
  const getAttributionText = (lang: string) => {
    return (
      GOOGLE_TRANSLATE_ATTRIBUTION[lang as keyof typeof GOOGLE_TRANSLATE_ATTRIBUTION] || GOOGLE_TRANSLATE_ATTRIBUTION.en
    );
  };

  // 번역 버튼 클릭 핸들러
  const handleTranslate = async () => {
    if (!onTranslate || !needsTranslation) return;
    setIsTranslating(true);
    try {
      const result = await onTranslate(text, targetLang);
      setTranslatedText(result);
      setShowTranslation(true);
      setHasTranslated(true);
    } catch (error) {
      console.error("Translation error:", error);
    } finally {
      setIsTranslating(false);
    }
  };

  // 원문 보기 버튼 클릭 핸들러
  const handleShowOriginal = () => {
    setShowTranslation(false);
  };

  // 화면에 렌더링할 텍스트 결정
  const displayText = showTranslation && translatedText ? translatedText : text;

  return (
    <Box
      component="span"
      sx={{
        whiteSpace: "pre-line",
        wordBreak: "break-word",
        ...sxProps,
      }}
    >
      {/* ShowMoreText 부분: 부모 스타일 그대로 받으려면 component="span" */}
      <Box component="span">
        <ReactShowMoreText
          lines={maxLines}
          more={
            <Box component="span" sx={{ cursor: "pointer", color: "primary.light" }}>
              {getShowMoreText(targetLang)}
            </Box>
          }
          less={""}
          expanded={expanded}
          keepNewLines={true}
        >
          {displayText}
        </ReactShowMoreText>
      </Box>

      {/* 번역 버튼 (인라인 텍스트처럼) */}
      {needsTranslation && (
        <Box component="span" sx={{ ml: 1, display: "inline-block", verticalAlign: "bottom" }}>
          {!showTranslation ? (
            <Button
              variant="text"
              size="small"
              disableRipple
              onClick={(e) => {
                e.preventDefault(); // Link 네비게이션 차단
                e.stopPropagation(); // 부모 컴포넌트로 이벤트 전파 차단
                handleTranslate();
              }}
              sx={{
                p: 0,
                m: 0,
                minWidth: "auto",
                textTransform: "none",
                fontSize: "0.875rem",
                lineHeight: 1.2,
                color: "text.secondary", // 약간 연한 회색
                fontWeight: "normal",
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "text.primary", // 호버 시 조금 더 진한 색
                },
              }}
              disabled={isTranslating}
            >
              {isTranslating ? getTranslatingText(targetLang) : getTranslateText(targetLang)}
            </Button>
          ) : (
            <Button
              variant="text"
              size="small"
              disableRipple
              onClick={(e) => {
                e.preventDefault(); // Link 네비게이션 차단
                e.stopPropagation(); // 부모 컴포넌트로 이벤트 전파 차단
                handleShowOriginal();
              }}
              sx={{
                p: 0,
                m: 0,
                minWidth: "auto",
                textTransform: "none",
                fontSize: "0.875rem",
                lineHeight: 1.2,
                color: "text.secondary",
                fontWeight: "normal",
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "text.primary",
                },
              }}
            >
              {getShowOriginalText(targetLang)}
            </Button>
          )}
        </Box>
      )}

      {/* 번역된 다음에 구글 저작 표기 (인라인, 최대한 작게) */}
      {showTranslation && translatedText && (
        <Box
          component="span"
          sx={{
            ml: 1,
            fontSize: "0.75rem",
            lineHeight: 1.2,
            fontStyle: "italic",
            color: "text.secondary",
          }}
        >
          {getAttributionText(targetLang)}
        </Box>
      )}
    </Box>
  );
};

export default GoogleTranslateShowMoreText;
